import type * as React from "react";

// TODO: migrate to react aria alternative
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { type VariantProps, cva } from "class-variance-authority";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full effect-color disabled:cursor-not-allowed active:scale-[0.98] transition-transform duration-300 ease-out",
  {
    variants: {
      variant: {
        primary:
          "bg-inverse text-inverse data-[state=active]:bg-darkest tw-outline-none focus-visible:tw-outline-focus",
        secondary:
          "bg-dark-new text-default data-[state=active]:bg-[#EDEBE8] tw-outline-none focus-visible:tw-outline-focus",
        "ghost-inverse":
          "text-inverse ring-offset-darkest data-[state=active]:bg-overlay-light tw-outline-none focus-visible:tw-outline-focus",
        "secondary-inverse":
          "text-inverse bg-overlay-light ring-offset-darkest data-[state=active]:bg-overlay-lighter tw-outline-none focus-visible:tw-outline-focus",
        "outline-inverse":
          "bg-transparent border border-overlay-light text-inverse data-[state=active]:bg-default data-[state=active]:text-default data-[state=active]:border-inverse tw-outline-none focus-visible:tw-outline-focus",
        outline:
          "border text-default border-darkest data-[state=active]:border-overlay-darkest tw-outline-none focus-visible:tw-outline-focus",
        ghost:
          "bg-transparent text-default data-[state=active]:bg-dark-new tw-outline-none focus-visible:tw-outline-focus",
        "ghost-warm":
          "bg-transparent text-default rounded-[12px] data-[state=active]:bg-dark-new tw-outline-none focus-visible:tw-outline-focus",
        "ghost-subtle":
          "bg-transparent text-default data-[state=active]:bg-dark-new tw-outline-none focus-visible:tw-outline-focus",
        elevated:
          "bg-default rounded-full shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_0_rgba(0,0,0,0.04)] tw-outline-none focus-visible:tw-outline-focus",
      },
      expand: {
        true: "w-full",
        false: "w-fit",
      },
      size: {
        "sm-light": "h-24 f-ui-05",
        sm: "h-28 f-paragraph-03 px-12",
        md: "h-40 f-paragraph-02 px-16",
        lg: "h-48 f-paragraph-02 px-20",
        "pill-sm": "w-24 h-24 f-ui-03",
        "pill-md": "w-32 h-32 f-ui-03",
        pill: "w-40 h-40 f-ui-03",
        "pill-lg": "size-48 f-ui-03",
        "pill-xl": "size-60 f-ui-03",
        "pill-responsive": "w-20 h-20 f-ui-03 md:w-32 md:h-32",
        "pill-responsive-lg": "w-28 h-28 md:w-40 md:h-40",
      },
      link: {
        true: "",
        false: "",
      },
      icon: {
        true: "",
        false: "",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "",
      },
      iconPos: {
        [ICON_ALIGN_OPTIONS.BEFORE]: "",
        [ICON_ALIGN_OPTIONS.AFTER]: "",
      },
    },
    compoundVariants: [
      {
        disabled: true,
        variant: "primary",
        className: "bg-darkest",
      },
      {
        disabled: true,
        variant: "elevated",
        className: "opacity-40",
      },
      // Icons
      {
        icon: false,
        size: ["sm-light"],
        className: "px-12",
      },
      {
        icon: true,
        size: ["lg", "md"],
        className: "gap-x-12",
      },
      {
        icon: true,
        size: ["sm-light"],
        iconPos: ICON_ALIGN_OPTIONS.BEFORE,
        className: "pl-4 pr-8",
      },
      {
        icon: true,
        size: ["sm-light"],
        iconPos: ICON_ALIGN_OPTIONS.AFTER,
        className: "pr-4 pl-8 gap-6",
      },
      {
        icon: true,
        size: ["sm"],
        iconPos: ICON_ALIGN_OPTIONS.BEFORE,
        className: "pl-8 gap-6",
      },
      {
        icon: true,
        size: ["sm"],
        iconPos: ICON_ALIGN_OPTIONS.AFTER,
        className: "pr-8 gap-6",
      },
      // Hover
      {
        link: false,
        variant: "primary",
        className: "enabled:hover:bg-darkest",
      },
      {
        link: true,
        variant: "primary",
        className: "hover:bg-darkest",
      },
      {
        link: false,
        variant: "secondary",
        className: "enabled:hover:bg-[#EDEBE8]",
      },
      {
        link: true,
        variant: "secondary",
        className: "hover:bg-[#EDEBE8]",
      },
      {
        link: false,
        variant: "ghost-inverse",
        className: "enabled:hover:bg-overlay-light",
      },
      {
        link: true,
        variant: "ghost-inverse",
        className: "hover:bg-overlay-light",
      },
      {
        link: false,
        variant: "secondary-inverse",
        className: "enabled:hover:bg-overlay-lighter",
      },
      {
        link: true,
        variant: "secondary-inverse",
        className: "hover:bg-overlay-lighter",
      },
      {
        link: false,
        variant: "outline-inverse",
        className: "enabled:hover:bg-default enabled:hover:text-default",
      },
      {
        link: true,
        variant: "outline-inverse",
        className: "hover:bg-default hover:text-default",
      },
      {
        link: false,
        variant: "outline",
        className: "enabled:hover:text-overlay-darker-plus-2 enabled:hover:border-overlay-darkest",
      },
      {
        link: true,
        variant: "outline",
        className: "hover:text-overlay-darker-plus-2 hover:border-overlay-darkest",
      },
      {
        link: false,
        variant: "ghost",
        className: "enabled:hover:bg-[#E9E6E2]/50",
      },
      {
        link: true,
        variant: "ghost",
        className: "hover:bg-[#E9E6E2]/50",
      },
      {
        link: false,
        variant: "ghost-warm",
        className: "enabled:hover:tw-bg-cream",
      },
      {
        link: true,
        variant: "ghost-warm",
        className: "hover:tw-bg-cream",
      },
      {
        link: false,
        variant: "ghost-subtle",
        className: "enabled:hover:tw-bg-black/5",
      },
      {
        link: true,
        variant: "ghost-subtle",
        className: "hover:tw-bg-black/5",
      },
      {
        link: false,
        variant: "elevated",
        className: "enabled:hover:bg-dark",
      },
      {
        link: true,
        variant: "elevated",
        className: "hover:bg-dark",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      icon: false,
      iconPos: ICON_ALIGN_OPTIONS.AFTER,
      expand: false,
      link: false,
      disabled: false,
    },
  },
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
    Omit<VariantProps<typeof buttonVariants>, "icon" | "iconPos"> {
  asChild?: boolean;
  icon?: React.ReactNode;
  iconPosition?: ICON_ALIGN_OPTIONS;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({
  className,
  variant,
  size,
  icon,
  iconPosition = ICON_ALIGN_OPTIONS.AFTER,
  asChild = false,
  children,
  expand,
  link,
  disabled = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(
        buttonVariants({
          variant,
          size,
          className,
          link,
          iconPos: iconPosition,
          icon: !!icon,
          expand,
          disabled,
        }),
      )}
      disabled={disabled || false}
      ref={ref}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {icon && iconPosition === ICON_ALIGN_OPTIONS.BEFORE && icon}
          {children}
          {icon && iconPosition === ICON_ALIGN_OPTIONS.AFTER && icon}
        </>
      )}
    </Comp>
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
